import { ProductionFittingComments } from "./production_fitting_comments.js";
/**
 * TechPackForm.tsx — Tech Pack form root + remaining sections
 *
 * Shared primitives: form_primitives.tsx
 * Extracted sections: size_range_section, measurements_section, bom_section
 */

import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  type PatternPieceRow,
  type ConstructionInstructionRow,
} from "../schema.js";
import { useStudio, useYArray } from "./StudioContext.js";
import {
  HighlightCtx,
  Section,
  TextField,
  TextAreaField,
  UploadBox,
  t,
  useYField,
  yPush,
  yUpdate,
  yDelete,
} from "./form_primitives.js";
import { StyleHeaderSection } from "./style_header_section.js";
import { formLabel } from "./techpack_form_contract.js";
import { SizeRangeSection } from "./size_range_section.js";
import { MeasurementsSection } from "./measurements_section.js";
import { BomSection } from "./bom_section.js";
import { CANONICAL_TO_STUDIO_IMAGE_KEY } from "../generated/shared/types/canonical_techpack_fields.js";

// ---------------------------------------------------------------------------
// 4. PATTERN PIECES
// ---------------------------------------------------------------------------

function mkPatternRow(): PatternPieceRow { return { id: uuidv4(), name: "", qty: "1", notes: "" }; }

function PatternPieces() {
  const { techPack, languageCode: lang } = useStudio();
  const yarray = techPack.pattern_pieces;
  const rows = useYArray(yarray);

  function upd(idx: number, f: keyof PatternPieceRow, v: string) {
    yUpdate(yarray, idx, { ...rows[idx], [f]: v });
  }
  function addPiece() {
    const row = mkPatternRow();
    yPush(yarray, row);
    requestAnimationFrame(() => document.querySelector<HTMLInputElement>(`[data-pattern-piece-id="${row.id}"]`)?.focus());
  }

  return (
    <Section title={t("s4", lang)}>
      <table className="sf-table">
        <thead>
          <tr>
            <th className="sf-th">{t("piece_name", lang)}</th>
            <th className="sf-th" style={{ width: 64 }}>{t("qty", lang)}</th>
            <th className="sf-th">{t("notes", lang)}</th>
            <th className="sf-th" style={{ width: 32 }} aria-label="Remove"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={row.id ?? idx}>
              {(["name", "qty", "notes"] as const).map((f) => (
                <td key={f} className="sf-td">
                  <input type="text" data-pattern-piece-id={f === "name" ? row.id : undefined} value={row[f]} onChange={(e) => upd(idx, f, e.target.value)} className="sf-cell" />
                </td>
              ))}
              <td className="sf-td">
                <button onClick={() => yDelete(yarray, idx)} className="sf-del-btn">×</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={addPiece} className="sf-add-btn" title={lang==="zh"?"新增一行版片名称、数量和备注":"Add a row for piece name, quantity, and notes"}>{t("add_piece", lang)}</button>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 5. DESCRIPTION
// ---------------------------------------------------------------------------

function DescriptionSection() {
  const { techPack, languageCode: lang } = useStudio();
  return (
    <Section title={t("s5", lang)}>
      <TextAreaField ymap={techPack.metadata} fieldKey="description" rows={4} />
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 6. CONSTRUCTION INSTRUCTIONS
// ---------------------------------------------------------------------------

function mkCsRow(): ConstructionInstructionRow {
  return { id: uuidv4(), piece: "", stitch_type: "", seam_finish: "", spi_thread: "", notes: "" };
}

const CS_COLS: { field: keyof ConstructionInstructionRow; lk: string }[] = [
  { field: "piece",       lk: "cs_piece"  },
  { field: "stitch_type", lk: "cs_stitch" },
  { field: "seam_finish", lk: "cs_seam"   },
  { field: "spi_thread",  lk: "cs_spi"    },
  { field: "notes",       lk: "notes"     },
];

function ConstructionInstructions() {
  const { techPack, languageCode: lang } = useStudio();
  const yarray = techPack.construction_instructions;
  const rows = useYArray(yarray);

  function upd(idx: number, f: keyof ConstructionInstructionRow, v: string) {
    yUpdate(yarray, idx, { ...rows[idx], [f]: v });
  }

  return (
    <>
      <div className="sf-table-wrap">
        <table className="sf-table">
          <thead>
            <tr>
              <th className="sf-th" style={{ width: 32 }}>#</th>
              {CS_COLS.map(({ lk }) => <th key={lk} className="sf-th">{t(lk, lang)}</th>)}
              <th className="sf-th" style={{ width: 32 }} aria-label="Remove"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row.id ?? idx}>
                <td className="sf-td sf-row-num">{idx + 1}</td>
                {CS_COLS.map(({ field }) => (
                  <td key={field} className="sf-td">
                    <input type="text" value={row[field]}
                      onChange={(e) => upd(idx, field, e.target.value)} className="sf-cell" />
                  </td>
                ))}
                <td className="sf-td">
                  <button onClick={() => yDelete(yarray, idx)} className="sf-del-btn">×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={() => yPush(yarray, mkCsRow())} className="sf-add-btn">{t("add_step", lang)}</button>
    </>
  );
}

// ---------------------------------------------------------------------------
// 8. CONSTRUCTION DETAILS
// ---------------------------------------------------------------------------

const PROD_TYPES = ["— Select —", "Woven", "Knit", "Denim", "Leather"] as const;

function ConstructionDetails() {
  const { techPack, languageCode: lang } = useStudio();
  const cd = techPack.construction_details;
  const [prodType, setProdType] = useYField(cd, "production_type");
  return (
    <>
      <div className="sf-grid-2 sf-mb2">
        <TextField ymap={cd} fieldKey="seam_allowance" label={t("cd_seam", lang)} placeholder="1.5 cm" />
        <TextField ymap={cd} fieldKey="tolerance"      label={t("cd_tolerance", lang)} placeholder="±0.5 cm" />
      </div>
      <div className="sf-mb2">
        <label className="sf-label">{t("cd_prod_type", lang)}</label>
        <select value={prodType} onChange={(e) => setProdType(e.target.value)} className="sf-select sf-construction-prod-type">
          {PROD_TYPES.map((v) => <option key={v} value={v}>{formLabel(v === "— Select —" ? "select" : v, lang)}</option>)}
        </select>
      </div>
      <TextField ymap={cd} fieldKey="construction_type" label={formLabel("construction_type",lang)} className="sf-mb2" />
      <div className="sf-mb2">
        <TextAreaField ymap={cd} fieldKey="special_techniques" label={t("cd_special", lang)}  rows={2} />
      </div>
      <div className="sf-mb2">
        <TextAreaField ymap={cd} fieldKey="additional_notes"   label={t("cd_add_notes", lang)} rows={2} />
      </div>
      <TextAreaField ymap={cd} fieldKey="factory_notes" label={t("cd_factory", lang)} rows={2} />
    </>
  );
}

// ---------------------------------------------------------------------------
// DESIGN IMAGES — 2×2 grid: photo front/back + tech sketch front/back
// ---------------------------------------------------------------------------

/**
 * All FIVE canonical image slots (contract §1). `design_sketch` was missing
 * from Studio's UI entirely -- DesignSketchTop() existed in form_primitives
 * but was never rendered, so the slot was unreachable.
 *
 * Yjs KEYS ARE NOT RENAMED HERE (founder decision 2026-09-17). Studio's live
 * documents store `tech_front`/`tech_back`; renaming them to the canonical
 * `tech_sketch_front`/`tech_sketch_back` would strand images in every
 * already-saved document behind a CRDT migration this pass does not take on.
 * CANONICAL_TO_STUDIO_IMAGE_KEY holds the mapping and the resolver translates
 * at Lock time.
 *
 * Layout: design_sketch spans the full width as the primary surface; the two
 * photos and two technical sketches sit beneath it in pairs. The grid sizes
 * by content rather than a hardcoded total height, so adding the fifth slot
 * does not crush the other four.
 */
function DesignImagesSection() {
  const { techPack, languageCode: lang } = useStudio();
  const key = CANONICAL_TO_STUDIO_IMAGE_KEY;
  return (
    <Section title={formLabel("images", lang)}>
      <div style={{ display: "grid", gap: 12 }}>
        <UploadBox ymap={techPack.images} fieldKey={key.design_sketch}
          label={formLabel("design_sketch", lang)} lang={lang} large />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <UploadBox ymap={techPack.images} fieldKey={key.photo_front}
            label={formLabel("photo_front", lang)} lang={lang} large />
          <UploadBox ymap={techPack.images} fieldKey={key.photo_back}
            label={formLabel("photo_back", lang)} lang={lang} large />
          <UploadBox ymap={techPack.images} fieldKey={key.tech_sketch_front}
            label={formLabel("tech_sketch_front", lang)} lang={lang} large />
          <UploadBox ymap={techPack.images} fieldKey={key.tech_sketch_back}
            label={formLabel("tech_sketch_back", lang)} lang={lang} large />
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// TechPackForm
// ---------------------------------------------------------------------------

export default function TechPackForm() {
  const [highlightedFields, setHighlightedFields] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    function onHighlight(e: Event) {
      const field = (e as CustomEvent<{ field: string }>).detail?.field;
      if (!field) return;
      setHighlightedFields(prev => new Set([...prev, field]));
      setTimeout(() => {
        setHighlightedFields(prev => { const next = new Set(prev); next.delete(field); return next; });
      }, 800);
    }
    window.addEventListener("sf:highlight-field", onHighlight);
    return () => window.removeEventListener("sf:highlight-field", onHighlight);
  }, []);

  // E-1 v2 render-gate: do NOT render the form (and therefore do NOT let its
  // sections self-seed placeholder rows/fields) until the initial load has
  // settled. This is the exhaustive fix for the regression — placeholders can no
  // longer pre-empt the REST/DB hydration. Covers every self-seeder in one place.
  const { hydrated, languageCode: lang } = useStudio();
  if (!hydrated) {
    return (
      <div style={{ padding: "40px", fontFamily: "Space Mono, monospace", fontSize: 12, color: "var(--text-muted)" }}>
        {formLabel("loading", lang)}
      </div>
    );
  }

  return (
    <HighlightCtx.Provider value={highlightedFields}>
      <DescriptionSection />
      <StyleHeaderSection />
      <SizeRangeSection />
      <DesignImagesSection />
      <MeasurementsSection />
      <BomSection />
      <Section title={formLabel("construction", lang)}>
        <ConstructionInstructions />
        <ConstructionDetails />
      </Section>
      <PatternPieces />
      <ProductionFittingComments />

    </HighlightCtx.Provider>
  );
}
